import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Breadcrumbs, Skeleton, Stack, Chip, Link,
  Dialog, DialogContent, DialogActions, TextField, DialogTitle,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TableChartIcon from '@mui/icons-material/TableChart';
import StorageIcon from '@mui/icons-material/Storage';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useSnackbar } from 'notistack';
import { useApp } from '../context/AppContext';
import { fetchRecord, fetchRecords, API_BASE_URL, approveFfSettlement, recordFfClearance, recordFfPayment, fetchApprovers } from '../services/api';
import { tableLabel } from '../config/tableLabels';
import { sidebarTooltips } from '../config/sidebarTooltips';
import { Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import DownloadIcon     from '@mui/icons-material/Download';
import CloseIcon        from '@mui/icons-material/Close';
import SendIcon         from '@mui/icons-material/Send';
import EditIcon         from '@mui/icons-material/Edit';
import PaymentIcon      from '@mui/icons-material/Payment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DataTable from '../components/DataTable';
import ElementEntryGrouped from '../components/ElementEntryGrouped';
import LeaveBalanceGrouped from '../components/LeaveBalanceGrouped';
import AttendanceGrouped from '../components/AttendanceGrouped';
import PayrollCostingGrouped from '../components/PayrollCostingGrouped';
import BalanceValueGrouped from '../components/BalanceValueGrouped';
import RunResultGrouped from '../components/RunResultGrouped';
import PayrollArtifactsGrouped from '../components/PayrollArtifactsGrouped';
import PayrollRunPage from '../components/PayrollRunPage';
import HolidayCalendarPage from '../components/HolidayCalendarPage';
import RecordForm from '../components/RecordForm';
import { generatePayslipPDF } from '../utils/generatePayslipPDF';
import { generateBankPaymentPDF } from '../utils/generateBankPaymentPDF';
import PayPeriodForm from '../components/PayPeriodForm';
import PayScheduleForm from '../components/PayScheduleForm';
import ElementSetupPage from '../components/ElementSetupPage';
import TaxGroupedView from '../components/TaxGroupedView';
import ConfirmDialog from '../components/ConfirmDialog';
import ViewRecordDialog from '../components/ViewRecordDialog';
import RejectionPopup from '../components/RejectionPopup';

const READ_ONLY_TABLES = new Set([
  'ELEMENT_ENTRY',
  'PAYMENT',
  'PAYSLIP',
  'BANK_PAYMENT_FILE',
  'ELEMENT_INPUT_VALUE',
  'ELEMENT_ELIGIBILITY',
  'PAYROLL_FORMULA',
]);

const NO_ADD_TABLES = new Set([
  'PAYROLL_RELATIONSHIP',
  'SALARY',
  'PAYROLL_COSTING',
]);

const NO_DELETE_TABLES = new Set([
  'SALARY',
  'PAYROLL_COSTING',
]);

export default function TablePage({ tableName, schema }) {
  const { state, selectTable, reloadRecords, loadTables, addRecord, editRecord, removeRecord,
          setActiveContext } = useApp();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [formOpen, setFormOpen]         = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deleteTarget, setDeleteTarget]  = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [formTableName, setFormTableName] = useState(tableName);
  const [formSchema, setFormSchema] = useState(schema);
  const [rejectionPopupOpen, setRejectionPopupOpen] = useState(false);
  const [rejectionPopupRecord, setRejectionPopupRecord] = useState(null);
  
  // Custom Alert Popup State
  const [alertPopup, setAlertPopup] = useState({ open: false, message: '' });

  // F&F payment state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentDialogRecord, setPaymentDialogRecord] = useState(null);
  const [paymentFields, setPaymentFields] = useState({
    payment_id: '',
    payment_mode: 'Bank Transfer',
    payment_date: new Date().toISOString().split('T')[0]
  });

  // F&F clearance state
  const [clearanceDialogOpen, setClearanceDialogOpen] = useState(false);
  const [clearanceDialogRecord, setClearanceDialogRecord] = useState(null);
  const [clearanceFields, setClearanceFields] = useState({
    clearance_action: 'Repayment',
    clearance_reference_no: '',
    clearance_remarks: ''
  });

  useEffect(() => {
    setFormTableName(tableName);
    setFormSchema(schema);
  }, [tableName, schema]);

  const isReadOnlyTable = READ_ONLY_TABLES.has(tableName);
  const canAddRecord = !isReadOnlyTable && !NO_ADD_TABLES.has(tableName);
  const canEditRecord = !isReadOnlyTable;
  const canDeleteRecord = !isReadOnlyTable && !NO_DELETE_TABLES.has(tableName);

  useEffect(() => { if (tableName) selectTable(tableName); }, [tableName]);



  const activeContextParams = () => {
    const ctx = state.activeContext;
    if (!ctx?.company_id) return '';
    const params = new URLSearchParams({ active_company_id: ctx.company_id });
    if (ctx.bg_id)      params.set('active_bg_id', ctx.bg_id);
    if (ctx.ldg_id)     params.set('active_ldg_id', ctx.ldg_id);
    if (ctx.payroll_id) params.set('active_payroll_id', ctx.payroll_id);
    return `?${params.toString()}`;
  };

  const handleAdd    = (initialData) => {
    setFormTableName(tableName);
    setFormSchema(schema);
    setEditingRecord(initialData && !initialData.nativeEvent ? initialData : null);
    setFormOpen(true);
  };
  const handleEdit   = (r) => {
    setFormTableName(tableName);
    setFormSchema(schema);
    setEditingRecord(r);
    setFormOpen(true);
  };
  const handleDelete = (r) => setDeleteTarget(r);
  const handleView   = (r) => {
    setFormTableName(tableName);
    setFormSchema(schema);
    setViewingRecord(r);
  };

  const handleApproveRequestClick = (row) => {
    setFormTableName('ADVANCE_SALARY');
    setFormSchema(schema);
    setEditingRecord({
      ...row,
      loan_status: 'APPROVED',
      approval_date: new Date().toISOString().split('T')[0],
      approved_by: state.user?.full_name || state.user?.username || 'Admin'
    });
    setFormOpen(true);
  };

  const handleRejectRequestClick = (row) => {
    setRejectionPopupRecord(row);
    setRejectionPopupOpen(true);
  };

  const handleRejectionSubmit = async (payload) => {
    try {
      const idToUpdate = rejectionPopupRecord.id ?? rejectionPopupRecord._id;
      await editRecord('ADVANCE_SALARY', idToUpdate, {
        loan_status: 'REJECTED',
        rejection_reason: payload.rejection_reason,
        rejected_by: payload.rejected_by,
        rejected_date: payload.rejected_date
      });
      enqueueSnackbar('Request rejected successfully', { variant: 'success' });
      setRejectionPopupOpen(false);
      setRejectionPopupRecord(null);
    } catch (e) {
      enqueueSnackbar(e.userMessage || e.response?.data?.message || e.message || 'Rejection failed', { variant: 'error' });
      throw e;
    }
  };

  // ── Download PDF (Payslip / Bank Payment File) ──────────────────────────
  const handleDownloadPDF = async (record) => {
    if (tableName === 'BANK_PAYMENT_FILE') {
      const id = record.file_id || record.id;
      if (!id) return enqueueSnackbar('Cannot download: missing file_id', { variant: 'warning' });
      const loadingSnackbar = enqueueSnackbar('Generating Payslip Data PDF...', { variant: 'info', persist: true });
      try {
        await generateBankPaymentPDF(record);
        closeSnackbar(loadingSnackbar);
        enqueueSnackbar('Payslip Data PDF generated successfully', { variant: 'success' });
      } catch (e) {
        closeSnackbar(loadingSnackbar);
        enqueueSnackbar('Error generating PDF: ' + (e.response?.data?.message || e.message), { variant: 'error' });
      }
    } else {
      const id = record.payslip_id || record.id;
      if (!id) return enqueueSnackbar('Cannot download: missing payslip_id', { variant: 'warning' });
      const loadingSnackbar = enqueueSnackbar('Generating PDF...', { variant: 'info', persist: true });
      try {
        await generatePayslipPDF(id);
        closeSnackbar(loadingSnackbar);
        enqueueSnackbar('Payslip generated successfully', { variant: 'success' });
      } catch (e) {
        closeSnackbar(loadingSnackbar);
        enqueueSnackbar('Error generating PDF: ' + (e.response?.data?.message || e.message), { variant: 'error' });
      }
    }
  };

  // ── Approve Salary (inline action) ────────────────────────────────────────
  const handleApproveSalary = async (salary) => {
    if (salary.approval_status === 'Approved') {
      enqueueSnackbar('Already approved', { variant: 'info' });
      return;
    }
    try {
      const id = salary.id ?? salary._id;
      const today = new Date().toISOString().split('T')[0];
      await editRecord('SALARY', id, {
        ...salary,
        approval_status: 'Approved',
        approved_by: 'ADMIN',
        approved_at: today,
        salary_status: 'active',
      });
      enqueueSnackbar(`Salary ${salary.salary_id} approved`, { variant: 'success' });
      
      // Reload records to reflect the backend changes where all previous salaries are set to is_current = false
      if (typeof reloadRecords === 'function') {
        reloadRecords('SALARY');
      }
    } catch (e) {
      enqueueSnackbar('Approval failed: ' + (e.userMessage || e.message), { variant: 'error' });
    }
  };

  // ── Download Form 16 PDF ─────────────────────────────────────────────────
  const handleDownloadForm16 = async (form16) => {
    const id = form16.form16_id || form16.id;
    if (!id) {
      enqueueSnackbar('Cannot download: missing form16_id', { variant: 'warning' });
      return;
    }
    
    const url = `${API_BASE_URL}/form16/${encodeURIComponent(id)}/pdf${activeContextParams()}`;
    const loadingSnackbar = enqueueSnackbar(`Generating Form 16 for ${form16.financial_year || id}...`, { variant: 'info', persist: true });

    let token = '';
    try {
      const auth = JSON.parse(localStorage.getItem('payroll.auth') || '{}');
      token = auth.token || '';
    } catch (e) {}

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        let errMsg = `Failed to download (${response.status})`;
        try {
          const errData = await response.json();
          errMsg = errData.message || errMsg;
        } catch (e) {}
        throw new Error(errMsg);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Form16_${id}.pdf`;
      if (contentDisposition && contentDisposition.includes('filename="')) {
        filename = contentDisposition.split('filename="')[1].split('"')[0];
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      closeSnackbar(loadingSnackbar);
      enqueueSnackbar('Form 16 downloaded successfully', { variant: 'success' });
    } catch (error) {
      closeSnackbar(loadingSnackbar);
      enqueueSnackbar(error.message || 'Error downloading Form 16', { variant: 'error' });
    }
  };

  // ── Manage Payroll: activate the company-scoped working context ──────────
  const handleManagePayroll = async (companyRow) => {
    if (!companyRow.default_bg_id || !companyRow.default_ldg_id) {
      enqueueSnackbar('Set Default BG and LDG on this company first', { variant: 'warning' });
      return;
    }
    try {
      const [bgRes, ldgRes] = await Promise.all([
        fetchRecord('BUSINESS_GROUP', companyRow.default_bg_id, {
          active_company_id: companyRow.company_id,
        }).catch(() => null),
        fetchRecord('LEGISLATION_DATA_GROUP', companyRow.default_ldg_id, {
          active_company_id: companyRow.company_id,
          active_ldg_id: companyRow.default_ldg_id,
        }).catch(() => null),
      ]);
      const bg  = bgRes?.data?.data;
      const ldg = ldgRes?.data?.data;
      const payrollRes = await fetchRecords('PAYROLL', {
        active_company_id: companyRow.company_id,
        active_bg_id: companyRow.default_bg_id,
        active_ldg_id: companyRow.default_ldg_id,
      }).catch(() => null);
      const payrolls = payrollRes?.data?.data || [];
      const payroll = payrolls.find(p => p.active_flag === 'Y' || p.status === 'active') || payrolls[0] || null;
      setActiveContext({
        company_id:   companyRow.company_id,
        bg_id:        companyRow.default_bg_id,
        ldg_id:       companyRow.default_ldg_id,
        payroll_id:   payroll?.payroll_id || null,
        company_name: companyRow.company_name || companyRow.company_id,
        bg_name:      bg?.business_group_name || companyRow.default_bg_id,
        ldg_name:     ldg?.legislation_name   || companyRow.default_ldg_id,
        payroll_name: payroll?.payroll_name || payroll?.payroll_id || null,
        set_at:       new Date().toISOString(),
      });
      await Promise.all([reloadRecords(tableName), loadTables()]);
      enqueueSnackbar(`Payroll context set: ${companyRow.company_name}`, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Failed to set context: ' + (e.userMessage || e.response?.data?.message || e.message), { variant: 'error' });
    }
  };

  const handleApproveRevision = async (rev) => {
    try {
      const id = rev.id ?? rev._id;
      let token = '';
      try {
        const auth = JSON.parse(localStorage.getItem('payroll.auth') || '{}');
        token = auth.token || '';
      } catch (e) {}

      const response = await fetch(`${API_BASE_URL}/salary_revision_history/${encodeURIComponent(id)}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const resData = await response.json();
      if (!response.ok) {
        throw new Error(resData.message || 'Approval failed.');
      }
      
      if (resData.data && resData.data.revisionNotification) {
        setAlertPopup({ open: true, message: resData.data.revisionNotification });
      }
      
      enqueueSnackbar(resData.message || 'Revision approved successfully', { variant: 'success' });
      reloadRecords('SALARY_REVISION_HISTORY');
    } catch (e) {
      enqueueSnackbar(e.message || 'Approval failed', { variant: 'error' });
    }
  };

  const handleRejectRevision = async (rev, reason) => {
    try {
      const id = rev.id ?? rev._id;
      let token = '';
      try {
        const auth = JSON.parse(localStorage.getItem('payroll.auth') || '{}');
        token = auth.token || '';
      } catch (e) {}

      const response = await fetch(`${API_BASE_URL}/salary_revision_history/${encodeURIComponent(id)}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ rejected_reason: reason })
      });
      const resData = await response.json();
      if (!response.ok) {
        throw new Error(resData.message || 'Rejection failed.');
      }
      enqueueSnackbar(resData.message || 'Revision rejected successfully', { variant: 'success' });
      reloadRecords('SALARY_REVISION_HISTORY');
      setRejectTarget(null);
      setRejectReason('');
    } catch (e) {
      enqueueSnackbar(e.message || 'Rejection failed', { variant: 'error' });
    }
  };

  const handleSubmitFF = async (row) => {
    try {
      const id = row.id ?? row._id ?? row.ff_id;
      await editRecord('FULL_FINAL_SETTLEMENT', id, {
        ...row,
        status: 'PENDING_APPROVAL'
      });
      enqueueSnackbar('Settlement submitted for approval', { variant: 'success' });
      if (typeof reloadRecords === 'function') {
        reloadRecords('FULL_FINAL_SETTLEMENT');
      }
    } catch (e) {
      enqueueSnackbar('Submission failed: ' + (e.userMessage || e.response?.data?.message || e.message), { variant: 'error' });
    }
  };

  const handleApproveFF = async (row) => {
    try {
      const id = row.id ?? row._id ?? row.ff_id;
      const res = await approveFfSettlement(id);
      if (res.data?.success) {
        enqueueSnackbar(res.data.message || 'Settlement approved successfully', { variant: 'success' });
        if (typeof reloadRecords === 'function') {
          reloadRecords('FULL_FINAL_SETTLEMENT');
        }
      }
    } catch (e) {
      enqueueSnackbar('Approval failed: ' + (e.userMessage || e.response?.data?.message || e.message), { variant: 'error' });
    }
  };

  const handleOpenPaymentDialog = (row) => {
    setPaymentDialogRecord(row);
    setPaymentFields({
      payment_id: `PAY-${row.ff_id || row.id || Date.now()}`,
      payment_mode: 'Bank Transfer',
      payment_date: new Date().toISOString().split('T')[0]
    });
    setPaymentDialogOpen(true);
  };

  const handleOpenClearanceDialog = (row) => {
    setClearanceDialogRecord(row);
    setClearanceFields({
      clearance_action: 'Repayment',
      clearance_reference_no: '',
      clearance_remarks: ''
    });
    setClearanceDialogOpen(true);
  };

  const handlePaymentSubmit = async () => {
    if (!paymentFields.payment_id) {
      enqueueSnackbar('Payment Reference ID is required', { variant: 'warning' });
      return;
    }
    try {
      const id = paymentDialogRecord.id ?? paymentDialogRecord._id ?? paymentDialogRecord.ff_id;
      const res = await recordFfPayment(id, paymentFields);
      if (res.data?.success) {
        enqueueSnackbar(res.data.message || 'Payment recorded successfully', { variant: 'success' });
        setPaymentDialogOpen(false);
        setPaymentDialogRecord(null);
        if (typeof reloadRecords === 'function') {
          reloadRecords('FULL_FINAL_SETTLEMENT');
        }
      }
    } catch (e) {
      enqueueSnackbar('Payment recording failed: ' + (e.userMessage || e.response?.data?.message || e.message), { variant: 'error' });
    }
  };

  const handleClearanceSubmit = async () => {
    if (!clearanceFields.clearance_action) {
      enqueueSnackbar('Clearance action is required', { variant: 'warning' });
      return;
    }
    try {
      const id = clearanceDialogRecord.id ?? clearanceDialogRecord._id ?? clearanceDialogRecord.ff_id;
      const res = await recordFfClearance(id, clearanceFields);
      if (res.data?.success) {
        enqueueSnackbar(res.data.message || 'Clearance recorded successfully', { variant: 'success' });
        setClearanceDialogOpen(false);
        setClearanceDialogRecord(null);
        if (typeof reloadRecords === 'function') {
          reloadRecords('FULL_FINAL_SETTLEMENT');
        }
      }
    } catch (e) {
      enqueueSnackbar('Clearance recording failed: ' + (e.userMessage || e.response?.data?.message || e.message), { variant: 'error' });
    }
  };

  const handleSave = async (data, andNew = false) => {
    try {
      const activeFormTable = formTableName || tableName;
      let resultData;
      if (editingRecord && (editingRecord.id || editingRecord._id)) {
        const idToUpdate = editingRecord.id ?? editingRecord._id;
        resultData = await editRecord(activeFormTable, idToUpdate, data);
        enqueueSnackbar('Record updated successfully', { variant: 'success' });
      } else {
        resultData = await addRecord(activeFormTable, data);
        enqueueSnackbar('Record created successfully', { variant: 'success' });
      }
      
      // Trigger immediate alert popups for auto-created statutory accounts
      if (resultData) {
        if (resultData.pfNotification) {
          setAlertPopup({ open: true, message: resultData.pfNotification });
        }
        if (resultData.esiNotification) {
          setAlertPopup({ open: true, message: resultData.esiNotification });
        }
      }

      if (!andNew) setFormOpen(false);
      if (activeFormTable !== tableName) {
        await reloadRecords(tableName);
      }
    } catch (e) {
      // Re-throw so RecordForm can show per-field errors when applicable
      const code = e.response?.data?.code;
      if (code === 'VALIDATION_ERROR') {
        enqueueSnackbar('Validation failed. Please check the highlighted fields.', { variant: 'error', autoHideDuration: 6000 });
      } else {
        enqueueSnackbar(e.userMessage || e.response?.data?.message || e.message || 'Save failed', { variant: 'error', autoHideDuration: 6000 });
      }
      throw e;
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await removeRecord(tableName, deleteTarget.id ?? deleteTarget._id);
      enqueueSnackbar('Record deleted', { variant: 'success' });
      setDeleteTarget(null);
    } catch (e) {
      enqueueSnackbar(e.userMessage || e.response?.data?.message || e.message || 'Delete failed', { variant: 'error' });
    }
  };

  if (state.loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton height={60} sx={{ mb: 2, borderRadius: 2 }} />
        {[...Array(8)].map((_, i) => <Skeleton key={i} height={44} sx={{ mb: 1, borderRadius: 1 }} />)}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Page Header ── */}
      <Box sx={{
        mb: 2, p: 2, bgcolor: 'white', borderRadius: '12px',
        border: '1.5px solid #E5E7EB',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }}
          justifyContent="space-between" gap={2}>
          <Box>
            {/* Breadcrumb */}
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="inherit" sx={{ fontSize: 14 }} />}
              sx={{ mb: 1, '& .MuiBreadcrumbs-separator': { color: '#94A3B8' } }}
            >
              <Typography fontSize={12} color="#9CA3AF" fontWeight={400}>Payroll HRMS</Typography>
              <Typography fontSize={12} color="#22c55e" fontWeight={500}>
                {tableLabel(tableName)}
              </Typography>
            </Breadcrumbs>

            {/* Title row */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{
                width: 38, height: 38, borderRadius: '10px',
                background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(34,197,94,0.3)',
              }}>
                <TableChartIcon sx={{ fontSize: 20, color: 'white' }} />
              </Box>
              <Box>
                <Typography fontWeight={600} color="#111827" lineHeight={1.2} fontSize={18}>
                  {tableLabel(tableName)}
                </Typography>
                {sidebarTooltips[tableName] && (
                  <Typography fontSize={12} color="text.secondary" mt={0.3}>
                    {sidebarTooltips[tableName]}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} mt={0.8}>
                  <Chip
                    icon={<StorageIcon sx={{ fontSize: '12px !important' }} />}
                    label={`${state.records.length} records`}
                    size="small"
                    sx={{ height: 20, fontSize: 11, bgcolor: '#f0fdf4', color: '#16a34a',
                      fontWeight: 600, '& .MuiChip-icon': { color: '#16a34a' } }}
                  />
                  <Chip
                    label={`${schema.length} columns`}
                    size="small"
                    sx={{ height: 20, fontSize: 11, bgcolor: '#F0FDF4', color: '#059669', fontWeight: 600 }}
                  />
                </Stack>
              </Box>
            </Stack>
          </Box>

          {tableName !== 'PAYROLL_RUN' && canAddRecord && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                px: 2.5, py: 1.1, borderRadius: '20px', fontWeight: 500, fontSize: 13.5,
                background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                boxShadow: '0 4px 16px rgba(34,197,94,0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg,#15803d,#16a34a)',
                  boxShadow: '0 6px 20px rgba(34,197,94,0.45)',
                },
                whiteSpace: 'nowrap',
              }}
            >
              {tableName === 'ELEMENT_TYPE'
                ? 'Add Salary Structure (Component)'
                : ['ELEMENT_INPUT_VALUE','ELEMENT_ELIGIBILITY','PAYROLL_FORMULA'].includes(tableName)
                  ? 'Add Element' : 'Add Record'}
            </Button>
          )}
        </Stack>
      </Box>

      {/* ── Data Table ── */}
      {tableName === 'HOLIDAY_CALENDAR' ? (
        <HolidayCalendarPage
          records={state.records}
          schema={schema}
          tableName={tableName}
          canEditRecord={canEditRecord}
          canDeleteRecord={canDeleteRecord}
          canAddRecord={canAddRecord}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleView={handleView}
          handleAdd={handleAdd}
        />
      ) : (tableName === 'ELEMENT_ENTRY' || tableName === 'ELEMENT_ENTRY_VALUE') ? (
        <ElementEntryGrouped
          entries={state.records}
          onEdit={isReadOnlyTable ? undefined : handleEdit}
          onDelete={isReadOnlyTable ? undefined : handleDelete}
          onAdd={isReadOnlyTable ? undefined : handleAdd}
        />
      ) : tableName === 'PAYROLL_RUN' ? (
        <PayrollRunPage
          runs={state.records}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={() => reloadRecords(tableName)}
        />
      ) : tableName === 'LEAVE_BALANCE' ? (
        <LeaveBalanceGrouped
          records={state.records}
          schema={schema}
          onEdit={canEditRecord ? handleEdit : undefined}
          onDelete={canDeleteRecord ? handleDelete : undefined}
          onAdd={canAddRecord ? handleAdd : undefined}
          onView={handleView}
        />
      ) : tableName === 'PAYROLL_COSTING' ? (
        <PayrollCostingGrouped
          records={state.records}
          schema={schema}
          onEdit={canEditRecord ? handleEdit : undefined}
          onDelete={canDeleteRecord ? handleDelete : undefined}
          onAdd={canAddRecord ? handleAdd : undefined}
          onView={handleView}
        />
      ) : tableName === 'BALANCE_VALUE' ? (
        <BalanceValueGrouped
          records={state.records}
          schema={schema}
          onEdit={canEditRecord ? handleEdit : undefined}
          onDelete={canDeleteRecord ? handleDelete : undefined}
          onAdd={canAddRecord ? handleAdd : undefined}
          onView={handleView}
        />
      ) : tableName === 'RUN_RESULT' ? (
        <RunResultGrouped
          records={state.records}
          schema={schema}
          onEdit={canEditRecord ? handleEdit : undefined}
          onDelete={canDeleteRecord ? handleDelete : undefined}
          onAdd={canAddRecord ? handleAdd : undefined}
          onView={handleView}
        />
      ) : tableName === 'TAX_DECLARATION' || tableName === 'TAX_REGIME_CONFIG' ? (
        <TaxGroupedView
          records={state.records}
          schema={schema}
          tableName={tableName}
          onEdit={canEditRecord ? handleEdit : undefined}
          onDelete={canDeleteRecord ? handleDelete : undefined}
          onAdd={canAddRecord ? handleAdd : undefined}
          onView={handleView}
        />
      ) : tableName === 'ATTENDANCE' ? (
        <AttendanceGrouped
          records={state.records}
          schema={schema}
          onEdit={canEditRecord ? handleEdit : undefined}
          onDelete={canDeleteRecord ? handleDelete : undefined}
          onAdd={canAddRecord ? handleAdd : undefined}
          onView={handleView}
        />
      ) : ['PAYMENT', 'PAYSLIP', 'BANK_PAYMENT_FILE'].includes(tableName) ? (
        <PayrollArtifactsGrouped
          records={state.records}
          tableName={tableName}
          schema={schema}
          onEdit={canEditRecord ? handleEdit : undefined}
          onDelete={canDeleteRecord ? handleDelete : undefined}
          onAdd={canAddRecord ? handleAdd : undefined}
          onView={handleView}
          onDownloadPDF={handleDownloadPDF}
        />
      ) : (
        <DataTable
          records={state.records}
          schema={schema}
          tableName={tableName}
          onEdit={tableName === 'ADVANCE_SALARY' ? undefined : (canEditRecord ? handleEdit : undefined)}
          onDelete={canDeleteRecord ? handleDelete : undefined}
          onView={handleView}
          onManagePayroll={tableName === 'COMPANY' ? handleManagePayroll : undefined}
          activeCompanyId={state.activeContext?.company_id}
          canCreate={canAddRecord}
          customRowAction={
            tableName === 'ADVANCE_SALARY'
              ? (row) => {
                  const isPending = row.loan_status === 'PENDING';
                  return (
                    <React.Fragment>
                      <Tooltip key="approve" title={isPending ? "Approve Request" : `Cannot Approve (Status: ${row.loan_status})`} arrow placement="top">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!isPending}
                            onClick={() => handleApproveRequestClick(row)}
                            sx={{
                              width: 28, height: 28, borderRadius: '7px', ml: 0.5,
                              color: '#16a34a', border: '1.5px solid #E5E7EB', bgcolor: 'white',
                              '&:hover': { bgcolor: '#f0fdf4', borderColor: '#16a34a' },
                              '&.Mui-disabled': { color: '#D1D5DB', bgcolor: '#FAFBFC', border: '1.5px solid #E5E7EB' },
                            }}
                          >
                            <CheckCircleIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip key="reject" title={isPending ? "Reject Request" : `Cannot Reject (Status: ${row.loan_status})`} arrow placement="top">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!isPending}
                            onClick={() => handleRejectRequestClick(row)}
                            sx={{
                              width: 28, height: 28, borderRadius: '7px', ml: 0.5,
                              color: '#DC2626', border: '1.5px solid #E5E7EB', bgcolor: 'white',
                              '&:hover': { bgcolor: '#FEF2F2', borderColor: '#DC2626' },
                              '&.Mui-disabled': { color: '#D1D5DB', bgcolor: '#FAFBFC', border: '1.5px solid #E5E7EB' },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip key="edit" title="Edit" arrow placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(row)}
                          sx={{
                            width: 28, height: 28, borderRadius: '7px', ml: 0.5,
                            color: '#22c55e', border: '1.5px solid #E5E7EB', bgcolor: 'white',
                            '&:hover': { bgcolor: '#f0fdf4', borderColor: '#22c55e' },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </React.Fragment>
                  );
                }
              : tableName === 'PAYSLIP'
              ? (row) => (
                  <Tooltip key="dl" title="Download Payslip PDF" arrow placement="top">
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadPDF(row)}
                      sx={{
                        width: 28, height: 28, borderRadius: '7px',
                        color: '#DC2626', border: '1.5px solid #E5E7EB', bgcolor: 'white',
                        '&:hover': { bgcolor: '#FEF2F2', borderColor: '#DC2626' },
                      }}
                    >
                      <PictureAsPdfIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )
              : tableName === 'FORM16_SUMMARY'
                ? (row) => (
                    <Tooltip key="dl16" title="Download Form 16 PDF" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadForm16(row)}
                        sx={{
                          width: 28, height: 28, borderRadius: '7px',
                          color: '#DC2626', border: '1.5px solid #E5E7EB', bgcolor: 'white',
                          '&:hover': { bgcolor: '#FEF2F2', borderColor: '#DC2626' },
                        }}
                      >
                        <DownloadIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )
                : tableName === 'SALARY'
                  ? isReadOnlyTable ? undefined : (row) => {
                      const isApproved = row.approval_status === 'Approved';
                      const pendingForAssign = state.records.filter(r => r.assignment_id === row.assignment_id && r.approval_status !== 'Approved');
                      const maxId = Math.max(0, ...pendingForAssign.map(r => r.id || 0));
                      const isLatestPending = (row.id === maxId);
                      const disabled = isApproved || !isLatestPending;
                      
                      let tooltipText = 'Approve salary';
                      if (isApproved) tooltipText = 'Already approved';
                      else if (!isLatestPending) tooltipText = 'Only the latest pending salary can be approved';

                      return (
                        <Tooltip
                          key="appr"
                          title={tooltipText}
                          arrow placement="top"
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={disabled}
                              onClick={() => handleApproveSalary(row)}
                              sx={{
                                width: 28, height: 28, borderRadius: '7px',
                                color: disabled ? '#9CA3AF' : '#16a34a',
                                border: '1.5px solid #E5E7EB', bgcolor: disabled ? '#F3F4F6' : 'white',
                                '&:hover': !disabled && { bgcolor: '#f0fdf4', borderColor: '#16a34a' },
                              }}
                            >
                              <CheckCircleIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      );
                    }
                  : tableName === 'SALARY_REVISION_HISTORY'
                    ? (row) => {
                        const isPending = row.revision_status === 'Pending Approval';
                        if (!isPending) return null;
                        return (
                          <Stack direction="row" spacing={1} key={`actions-${row.id || row.revision_id}`}>
                            <Tooltip title="Approve Revision" arrow placement="top">
                              <IconButton
                                size="small"
                                onClick={() => handleApproveRevision(row)}
                                sx={{
                                  width: 28, height: 28, borderRadius: '7px',
                                  color: '#16a34a', border: '1.5px solid #E5E7EB', bgcolor: 'white',
                                  '&:hover': { bgcolor: '#f0fdf4', borderColor: '#16a34a' },
                                }}
                              >
                                <CheckCircleIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject Revision" arrow placement="top">
                              <IconButton
                                size="small"
                                onClick={() => { setRejectTarget(row); setRejectReason(''); }}
                                sx={{
                                  width: 28, height: 28, borderRadius: '7px',
                                  color: '#DC2626', border: '1.5px solid #E5E7EB', bgcolor: 'white',
                                  '&:hover': { bgcolor: '#FEF2F2', borderColor: '#DC2626' },
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        );
                      }
                    : tableName === 'FULL_FINAL_SETTLEMENT'
                      ? (row) => {
                          const isDraft = row.status === 'DRAFT';
                          const isPending = row.status === 'PENDING_APPROVAL';
                          const isApproved = row.status === 'APPROVED';
                          const canApprove = isPending || isDraft;
                          const canSubmit = isDraft;

                          const netPayable = Number(row.net_payable) || 0;
                          const needsPayment = isApproved && netPayable > 0 && row.payment_status !== 'Paid';
                          const needsClearance = isApproved && netPayable < 0 && row.clearance_status !== 'Cleared';

                          if (!canApprove && !canSubmit && !needsPayment && !needsClearance) return null;

                          return (
                            <Stack direction="row" spacing={1} key={`actions-${row.id || row.ff_id}`}>
                              {canSubmit && (
                                <Tooltip title="Submit for Approval" arrow placement="top">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSubmitFF(row)}
                                    sx={{
                                      width: 28, height: 28, borderRadius: '7px',
                                      color: '#2563eb', border: '1.5px solid #E5E7EB', bgcolor: 'white',
                                      '&:hover': { bgcolor: '#eff6ff', borderColor: '#2563eb' },
                                    }}
                                  >
                                    <SendIcon sx={{ fontSize: 13 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canApprove && (
                                <Tooltip title="Approve Settlement" arrow placement="top">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleApproveFF(row)}
                                    sx={{
                                      width: 28, height: 28, borderRadius: '7px',
                                      color: '#16a34a', border: '1.5px solid #E5E7EB', bgcolor: 'white',
                                      '&:hover': { bgcolor: '#f0fdf4', borderColor: '#16a34a' },
                                    }}
                                  >
                                    <CheckCircleIcon sx={{ fontSize: 13 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {needsPayment && (
                                <Tooltip title="Disburse Payment" arrow placement="top">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenPaymentDialog(row)}
                                    sx={{
                                      width: 28, height: 28, borderRadius: '7px',
                                      color: '#9333ea', border: '1.5px solid #E5E7EB', bgcolor: 'white',
                                      '&:hover': { bgcolor: '#faf5ff', borderColor: '#9333ea' },
                                    }}
                                  >
                                    <PaymentIcon sx={{ fontSize: 13 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {needsClearance && (
                                <Tooltip title="Clear Dues" arrow placement="top">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenClearanceDialog(row)}
                                    sx={{
                                      width: 28, height: 28, borderRadius: '7px',
                                      color: '#ea580c', border: '1.5px solid #E5E7EB', bgcolor: 'white',
                                      '&:hover': { bgcolor: '#fff7ed', borderColor: '#ea580c' },
                                    }}
                                  >
                                    <AssignmentTurnedInIcon sx={{ fontSize: 13 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          );
                        }
                    : undefined
          }
        />
      )}

      {tableName === 'PAY_PERIOD' ? (
        <PayPeriodForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSave={handleSave}
          record={editingRecord}
          existingRecords={state.records}
        />
      ) : tableName === 'PAYROLL' ? (
        <PayScheduleForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSave={data => handleSave(data)}
          record={editingRecord}
          existingRecords={state.records}
        />
      ) : ['ELEMENT_TYPE','ELEMENT_INPUT_VALUE','ELEMENT_ELIGIBILITY','PAYROLL_FORMULA'].includes(tableName) ? (
        <ElementSetupPage
          open={formOpen}
          onClose={() => setFormOpen(false)}
          record={editingRecord}
          onSaved={async () => {
            setFormOpen(false);
            // Element create/update touches PAYROLL_FORMULA, ELEMENT_INPUT_VALUE
            // and ELEMENT_ELIGIBILITY too — reload the active table from DB and
            // refresh sidebar counts so every related table reflects the change.
            await Promise.all([reloadRecords(tableName), loadTables()]);
          }}
        />
      ) : (
        <RecordForm
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setFormTableName(tableName);
            setFormSchema(schema);
          }}
          onSave={handleSave}
          schema={formSchema || schema}
          record={editingRecord}
          tableName={formTableName || tableName}
          existingRecords={state.records}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        message={`Delete this record? This action cannot be undone.`}
      />

      <ViewRecordDialog
        open={!!viewingRecord}
        onClose={() => {
          setViewingRecord(null);
          setFormTableName(tableName);
          setFormSchema(schema);
        }}
        schema={formSchema || schema}
        record={viewingRecord}
        tableName={formTableName || tableName}
      />

      {/* Reject Reason Dialog */}
      <Dialog
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Top accent */}
          <Box sx={{
            height: 4,
            background: 'linear-gradient(90deg, #DC2626, #EF4444)',
          }} />

          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <Typography fontWeight={800} fontSize={18} color="text.primary" mb={1} textAlign="center">
              Reject Salary Revision
            </Typography>
            <Typography fontSize={13} color="text.secondary" lineHeight={1.6} mb={2} textAlign="center">
              Please provide a reason for rejecting the salary revision of {rejectTarget?.assignment_id}.
            </Typography>
            
            <TextField
              label="Rejection Reason"
              placeholder="e.g. CTC details mismatch or requires further review"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              size="small"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              InputProps={{ sx: { fontSize: 13, borderRadius: '8px' } }}
              InputLabelProps={{ sx: { fontSize: 13 } }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 3, pb: 3, pt: 0,
          display: 'flex', flexDirection: 'column', gap: 1,
        }}>
          <Button
            onClick={() => handleRejectRevision(rejectTarget, rejectReason)}
            fullWidth
            variant="contained"
            disabled={!rejectReason.trim()}
            sx={{
              py: 1.2, fontWeight: 700, fontSize: 14,
              bgcolor: '#DC2626', color: 'white', borderRadius: '10px',
              boxShadow: '0 4px 14px rgba(220,38,38,0.35)',
              '&:hover': { bgcolor: '#B91C1C', boxShadow: '0 6px 18px rgba(220,38,38,0.45)' },
              '&.Mui-disabled': { bgcolor: '#F3F4F6' }
            }}
          >
            Reject Revision
          </Button>
          <Button
            onClick={() => setRejectTarget(null)}
            fullWidth
            variant="outlined"
            sx={{
              py: 1.2, fontWeight: 600, fontSize: 14,
              borderColor: '#E2E8F0', color: '#64748B', borderRadius: '10px',
              '&:hover': { bgcolor: '#F8FAFC', borderColor: '#94A3B8' },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>



      <RejectionPopup
        open={rejectionPopupOpen}
        record={rejectionPopupRecord}
        onClose={() => {
          setRejectionPopupOpen(false);
          setRejectionPopupRecord(null);
        }}
        onSubmit={handleRejectionSubmit}
      />

      {/* Disburse Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Top accent */}
          <Box sx={{
            height: 4,
            background: 'linear-gradient(90deg, #9333ea, #a855f7)',
          }} />

          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <Typography fontWeight={800} fontSize={18} color="text.primary" mb={1} textAlign="center">
              Disburse Payment
            </Typography>
            <Typography fontSize={13} color="text.secondary" lineHeight={1.6} mb={3} textAlign="center">
              Record settlement disbursement details for employee {paymentDialogRecord?.assignment_id}.
              <br />
              <strong>Net Payable:</strong> {paymentDialogRecord?.currency || 'INR'} {paymentDialogRecord?.net_payable}
            </Typography>
            
            <Stack spacing={2.5}>
              <TextField
                label="Payment Reference ID"
                placeholder="e.g. TXN100028827"
                fullWidth
                variant="outlined"
                size="small"
                value={paymentFields.payment_id}
                onChange={(e) => setPaymentFields({ ...paymentFields, payment_id: e.target.value })}
                InputProps={{ sx: { fontSize: 13, borderRadius: '8px' } }}
                InputLabelProps={{ sx: { fontSize: 13 } }}
                required
              />

              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: 13 }}>Payment Mode</InputLabel>
                <Select
                  value={paymentFields.payment_mode}
                  label="Payment Mode"
                  onChange={(e) => setPaymentFields({ ...paymentFields, payment_mode: e.target.value })}
                  sx={{ fontSize: 13, borderRadius: '8px' }}
                >
                  <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Cheque">Cheque</MenuItem>
                  <MenuItem value="Online Transfer">Online Transfer</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Payment Date"
                type="date"
                fullWidth
                variant="outlined"
                size="small"
                value={paymentFields.payment_date}
                onChange={(e) => setPaymentFields({ ...paymentFields, payment_date: e.target.value })}
                InputProps={{ sx: { fontSize: 13, borderRadius: '8px' } }}
                InputLabelProps={{ sx: { fontSize: 13 }, shrink: true }}
              />
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 3, pb: 3, pt: 1,
          display: 'flex', flexDirection: 'column', gap: 1,
        }}>
          <Button
            onClick={handlePaymentSubmit}
            fullWidth
            variant="contained"
            disabled={!paymentFields.payment_id.trim()}
            sx={{
              py: 1.2, fontWeight: 700, fontSize: 14,
              bgcolor: '#9333ea', color: 'white', borderRadius: '10px',
              boxShadow: '0 4px 14px rgba(147,51,234,0.35)',
              '&:hover': { bgcolor: '#7e22ce', boxShadow: '0 6px 18px rgba(147,51,234,0.45)' },
              '&.Mui-disabled': { bgcolor: '#F3F4F6' }
            }}
          >
            Record Payment
          </Button>
          <Button
            onClick={() => setPaymentDialogOpen(false)}
            fullWidth
            variant="outlined"
            sx={{
              py: 1.2, fontWeight: 600, fontSize: 14,
              borderColor: '#E2E8F0', color: '#64748B', borderRadius: '10px',
              '&:hover': { bgcolor: '#F8FAFC', borderColor: '#94A3B8' },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Dues Dialog */}
      <Dialog
        open={clearanceDialogOpen}
        onClose={() => setClearanceDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Top accent */}
          <Box sx={{
            height: 4,
            background: 'linear-gradient(90deg, #ea580c, #f97316)',
          }} />

          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <Typography fontWeight={800} fontSize={18} color="text.primary" mb={1} textAlign="center">
              Clear Outstanding Dues
            </Typography>
            <Typography fontSize={13} color="text.secondary" lineHeight={1.6} mb={3} textAlign="center">
              Record waiver or repayment for negative settlement of employee {clearanceDialogRecord?.assignment_id}.
              <br />
              <strong>Due Amount:</strong> {clearanceDialogRecord?.currency || 'INR'} {Math.abs(Number(clearanceDialogRecord?.net_payable) || 0)}
            </Typography>
            
            <Stack spacing={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: 13 }}>Clearance Action</InputLabel>
                <Select
                  value={clearanceFields.clearance_action}
                  label="Clearance Action"
                  onChange={(e) => setClearanceFields({ ...clearanceFields, clearance_action: e.target.value })}
                  sx={{ fontSize: 13, borderRadius: '8px' }}
                >
                  <MenuItem value="Repayment">Repayment (Received from Employee)</MenuItem>
                  <MenuItem value="Waiver">Waiver (Forgiven by Company)</MenuItem>
                  <MenuItem value="Recovery Agreement">Recovery Agreement (Settled Off-Platform)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Reference Number / Receipt ID"
                placeholder="e.g. RCPT200921"
                fullWidth
                variant="outlined"
                size="small"
                value={clearanceFields.clearance_reference_no}
                onChange={(e) => setClearanceFields({ ...clearanceFields, clearance_reference_no: e.target.value })}
                InputProps={{ sx: { fontSize: 13, borderRadius: '8px' } }}
                InputLabelProps={{ sx: { fontSize: 13 } }}
              />

              <TextField
                label="Clearance Remarks"
                placeholder="Details of cash receipt, waiver approval, etc."
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                size="small"
                value={clearanceFields.clearance_remarks}
                onChange={(e) => setClearanceFields({ ...clearanceFields, clearance_remarks: e.target.value })}
                InputProps={{ sx: { fontSize: 13, borderRadius: '8px' } }}
                InputLabelProps={{ sx: { fontSize: 13 } }}
              />
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 3, pb: 3, pt: 1,
          display: 'flex', flexDirection: 'column', gap: 1,
        }}>
          <Button
            onClick={handleClearanceSubmit}
            fullWidth
            variant="contained"
            sx={{
              py: 1.2, fontWeight: 700, fontSize: 14,
              bgcolor: '#ea580c', color: 'white', borderRadius: '10px',
              boxShadow: '0 4px 14px rgba(234,88,12,0.35)',
              '&:hover': { bgcolor: '#c2410c', boxShadow: '0 6px 18px rgba(234,88,12,0.45)' },
            }}
          >
            Record Clearance
          </Button>
          <Button
            onClick={() => setClearanceDialogOpen(false)}
            fullWidth
            variant="outlined"
            sx={{
              py: 1.2, fontWeight: 600, fontSize: 14,
              borderColor: '#E2E8F0', color: '#64748B', borderRadius: '10px',
              '&:hover': { bgcolor: '#F8FAFC', borderColor: '#94A3B8' },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
